/**
 * MapViewport - Handles map display, zoom, pan, and touch interactions
 *
 * Responsibilities:
 * - Creates container and map DOM elements
 * - Manages position state (x, y, scale)
 * - Handles mouse drag with momentum
 * - Handles touch drag and pinch-to-zoom
 * - Normalizes coordinates to stay within bounds
 *
 * jQuery-free implementation using native DOM APIs
 */

import { div } from '../utils/dom';

/**
 * Insets describe the gap (in content pixels) between the full
 * coordinate-space (contentWidth × contentHeight) and the actual
 * SVG artwork.  The SVG is scaled to fit inside the coordinate
 * space while preserving its aspect ratio, so either horizontal
 * or vertical gaps appear.
 *
 * Example: coordinate space 1200×500, SVG viewBox 623.6×455
 *  → SVG fits by height → effective width ≈ 685, centred
 *  → left = right ≈ 257.5,  top = bottom = 0
 */

type ZoomStep = number;

export interface Space {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface Area {
  x: number;
  y: number;
  spaceAbove: number;
  spaceBelow: number;
  spaceLeft: number;
  spaceRight: number;
}

interface MapViewportOptions {
  contentWidth: number;
  contentHeight: number;
  maxScale: number;
  zoomSteps: ZoomStep;
  zoomMargin: number;
}

interface SetViewOptions {
  zoomStep?: number | null;  // null = keep current step
  duration?: number;         // default 400ms
  verticalOffset?: number;   // screen pixels to shift target below viewport center
  preserveSpace?: Space;   // whether to preserve vertical space for the offset (default: true)
}

interface MapViewportCallbacks {
  onZoomChange: (scale: number, fitScale: number) => void;
  switchLevel: (level: string) => void;
  onManualZoom?: () => void;
}

interface Position {
  x: number;
  y: number;
}

const DEFAULT_OPTIONS:MapViewportOptions = {
  contentWidth: 1000,
  contentHeight: 1000,
  maxScale: 4,
  zoomSteps: 3,
  zoomMargin: 50
}

const CLASSNAME_MAPPPLIC_CONTAINER = 'mapplic-container';
const CLASSNAME_MAPPPLIC_MAP = 'mapplic-map';

export class MapViewport {
  // DOM Elements
  public readonly rootElem: HTMLElement;
  private readonly mapElem: HTMLElement;

  // Position state
  public x: number = 0;
  public y: number = 0;
  public scale: number = 1;
  public fitScale: number = 1;
  public zoomStep: ZoomStep = 0;

  /** Content coordinate (0..1) currently at viewport center — derived from x/scale. */
  private get xPerc(): number {
    return (this.rootElem.offsetWidth * 0.5 - this.x) / (this.scale * this.options.contentWidth);
  }

  /** Content coordinate (0..1) currently at viewport center — derived from y/scale. */
  private get yPerc(): number {
    return (this.rootElem.offsetHeight * 0.5 - this.y) / (this.scale * this.options.contentHeight);
  }

  // Interaction state
  public dragging: boolean = false;

  // Internal state
  private preserveArea: Area | null = null;
  private oldWidth: number = 0;
  private oldHeight: number = 0;
  private contentInsets: Space = { top: 0, right: 0, bottom: 0, left: 0 };
  private zoomTimeout: ReturnType<typeof setTimeout> | null = null;
  private layers: Map<string, HTMLElement> = new Map();

  // Event handler references (for cleanup)
  private touchMoveHandler: ((e: TouchEvent) => void) | null = null;
  private touchEndHandler: ((e: TouchEvent) => void) | null = null;

  // Configuration
  private options: MapViewportOptions;
  private callbacks: MapViewportCallbacks;

  constructor(
    options: Partial<MapViewportOptions>,
    callbacks: MapViewportCallbacks
  ) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.callbacks = callbacks;

    // Create DOM structure (caller is responsible for appending to DOM)
    this.rootElem = div({
      className: CLASSNAME_MAPPPLIC_CONTAINER,
      attributes: {
        tabindex: '0',
        role: 'group',
        'aria-label': 'Lageplan. Pfeiltasten zum Bewegen, Plus/Minus zum Zoomen.'
      }
    });
    this.mapElem = div({ className: CLASSNAME_MAPPPLIC_MAP });
    this.mapElem.style.setProperty('--zoom-margin', `${this.options.zoomMargin || 0}px`);

    this.mapElem.style.width = this.options.contentWidth + 'px';
    this.mapElem.style.height = this.options.contentHeight + 'px';

    this.rootElem.appendChild(this.mapElem);
  }

  public init(): void {
    this.calcFitScale();
    this.resetView();
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  /**
   * Reset view to centered position at minimum zoom
   */
  public resetView(): void {
    this.setView(0.5, 0.5, { zoomStep: 0, duration: 0 });
  }

  public setPreserveArea(area: Area | null, duration: number = 400): void {
    this.preserveArea = area;

    // Re-clamp to new boundaries so the map doesn't stay
    // outside the allowed area until the next user interaction.
    const clampedX = this.clampX(this.x);
    const clampedY = this.clampY(this.y);

    if (clampedX !== this.x || clampedY !== this.y) {
      this.x = clampedX;
      this.y = clampedY;
      this.applyTransform(this.x, this.y, undefined, duration);
    }
  }

  /**
   * Set viewport position and zoom level.
   * Centers the content coordinate (x, y) in the viewport.
   *
   * xPerc/yPerc are content coordinates (0..1).
   */
  public setView(xPerc: number, yPerc: number, options?: SetViewOptions): void {
    const zoomStep = options?.zoomStep !== undefined ? options.zoomStep : null;
    const duration = options?.duration ?? 400;
    const verticalOffset = options?.verticalOffset ?? 0;

    if (options?.preserveSpace) {
      this.setPreserveArea({
        x: xPerc,
        y: yPerc,
        spaceAbove: options.preserveSpace.top,
        spaceBelow: options.preserveSpace.bottom,
        spaceLeft: options.preserveSpace.left,
        spaceRight: options.preserveSpace.right
      });
    }

    const { zoomStep: targetZoomStep, scale } = this.calcScaleForZoomStep(zoomStep);
    this.scale = scale;
    this.zoomStep = targetZoomStep;

    let targetXPerc = xPerc;
    let targetYPerc = yPerc;

    if (verticalOffset !== 0) {
      targetYPerc = yPerc - verticalOffset / (scale * this.options.contentHeight)
    }

    const xPx = this.rootElem.offsetWidth * 0.5 - this.scale * this.options.contentWidth * targetXPerc;
    const yPx = this.rootElem.offsetHeight * 0.5 - this.scale * this.options.contentHeight * targetYPerc;
    
    this.x = this.clampX(xPx);
    this.y = this.clampY(yPx);

    this.applyTransform(this.x, this.y, scale, duration);
  }

  /**
   * Handle window resize — recalculate fitScale and reapply
   * the current view so clamping works with the new dimensions.
   */
  public handleResize(): void {
    this.fitScale = this.rootElem.offsetHeight / this.options.contentHeight;
    
    if (this.oldWidth !== this.rootElem.offsetWidth || this.oldHeight !== this.rootElem.offsetHeight) {
      this.oldWidth = this.rootElem.offsetWidth;
      this.oldHeight = this.rootElem.offsetHeight;
      this.setView(this.xPerc, this.yPerc, { zoomStep: this.zoomStep, duration: 0 });
    }
  }

  /**
   * Set content insets for the currently active level.
   * Insets shrink the pannable area so the user cannot scroll
   * into the empty gaps beside the actual SVG artwork.
   */
  public setContentInsets(insets: Space): void {
    this.contentInsets = insets;
    this.rootElem.style.setProperty('--inset-left', `${insets.left.toFixed(0)}px`);
    this.rootElem.style.setProperty('--inset-right', `${insets.right.toFixed(0)}px`);
    this.rootElem.style.setProperty('--inset-top', `${insets.top.toFixed(0)}px`);
    this.rootElem.style.setProperty('--inset-bottom', `${insets.bottom.toFixed(0)}px`);
  }

  /**
   * Add drag, pinch-zoom and keyboard controls
   */
  public addControls(): void {
    this.mapElem.setAttribute('data-zoomable', '');

    // Disable native drag
    document.ondragstart = () => false;

    this.setupDrag();
    this.setupWheelZoom();
    this.setupPinchZoom();
    this.setupKeyboardNavigation();
  }

  /**
   * Step zoom in, keeping current center
   */
  public zoomIn(): void {
    const targetZoomStep = this.zoomStep + 1;
    if (targetZoomStep > this.options.zoomSteps) {
      return;
    }
    this.setView(this.xPerc, this.yPerc, { zoomStep: targetZoomStep });
  }

  /**
   * Step zoom out, keeping current center
   */
  public zoomOut(): void {
    const targetZoomStep = this.zoomStep - 1;
    if (targetZoomStep < 0) {
      return;
    }
    this.setView(this.xPerc, this.yPerc, { zoomStep: targetZoomStep });
  }

  /**
   * Insert a control element (e.g. zoom buttons, level switcher)
   * before the map layer, so it overlays the map content.
   */
  public addControl(element: HTMLElement): void {
    this.rootElem.insertBefore(element, this.mapElem);
  }

  /**
   * Append an element to the map (e.g. tooltip).
   */
  public addToMap(element: HTMLElement): void {
    this.mapElem.appendChild(element);
  }

  /**
   * Register a layer (e.g. a floor) and append it to the map.
   * Layers are mutually exclusive — only one is visible at a time.
   */
  public addLayer(id: string, element: HTMLElement): void {
    element.style.display = 'none';
    this.layers.set(id, element);
    this.mapElem.appendChild(element);
  }

  /**
   * Show a layer by id and hide all others.
   * Returns the shown element, or null if the id is unknown.
   */
  public showLayer(id: string): HTMLElement | null {
    const target = this.layers.get(id);
    if (!target) return null;

    // Already visible — nothing to do
    if (target.style.display !== 'none') return target;

    for (const layer of this.layers.values()) {
      layer.style.display = 'none';
    }
    target.style.display = 'block';
    return target;
  }

  /**
   * Hide a single layer by id.
   */
  public hideLayer(id: string): void {
    const layer = this.layers.get(id);
    if (layer) layer.style.display = 'none';
  }

  /**
   * Get a layer element by id.
   */
  public getLayer(id: string): HTMLElement | null {
    return this.layers.get(id) ?? null;
  }

  /**
   * Query a single element inside the map (e.g. for finding SVG landmarks).
   */
  public queryMap<T extends Element = Element>(selector: string): T | null {
    return this.mapElem.querySelector<T>(selector);
  }

  public setZoomDuration(duration: number): void {
    this.mapElem.style.setProperty('--zoom-duration', `${duration / 1000}s`);
  }

  private calcFitScale(): void {
    this.fitScale = this.rootElem.offsetHeight / this.options.contentHeight;
  }

  /**
   * Apply position and scale to map via CSS custom properties.
   */
  private applyTransform(x: number, y: number, scale?: number, duration: number = 0): void {
    this.setZoomDuration(duration);

    this.mapElem.style.setProperty('--map-x', `${x.toFixed(3)}px`);
    this.mapElem.style.setProperty('--map-y', `${y.toFixed(3)}px`);

    if (scale) {
      this.mapElem.style.setProperty('--map-scale', this.scale.toFixed(3));
      this.notifyZoomChange(scale);
    }
  }

  /**
   * Debounced zoom-change notification.
   * Waits 200ms after the last call so pinch-zoom doesn't
   * fire callbacks on every frame.
   */
  private notifyZoomChange(scale: number): void {
    if (this.zoomTimeout) {
      clearTimeout(this.zoomTimeout);
    }
    this.zoomTimeout = setTimeout(() => {
      this.callbacks.onZoomChange(scale, this.fitScale);
    }, 200);
  }

  /**
   * Get element offset (replacement for jQuery .offset())
   */
  public getOffset(): { left: number; top: number } {
    const rect = this.rootElem.getBoundingClientRect();
    return {
      left: rect.left + window.scrollX,
      top: rect.top + window.scrollY
    };
  }

  // ==================== PRIVATE METHODS ====================

  /**
   * Normalize X coordinate to stay within bounds.
   * Content insets shrink the effective content area so the user
   * cannot pan into the empty gaps beside the SVG artwork.
   */
  private clampX(x: number): number {
    let maxX = (this.options.zoomMargin - this.contentInsets.left) * this.scale;
    let minX = this.rootElem.offsetWidth - (this.options.contentWidth + this.options.zoomMargin - this.contentInsets.right) * this.scale;

    if (this.preserveArea) {
      const xPx = this.options.contentWidth * this.preserveArea.x * this.scale;
      maxX += Math.max(0, this.preserveArea.spaceLeft - xPx);
      minX -= Math.max(0, this.preserveArea.spaceRight + xPx - this.options.contentWidth * this.scale);
    }

    // When the container is wider than the content, center instead of clamping
    if (minX > maxX) return (minX + maxX) / 2;
    return Math.max(minX, Math.min(maxX, x));
  }

  private clampY(y: number): number {
    let maxY = (this.options.zoomMargin + this.contentInsets.top) * this.scale;
    let minY = this.rootElem.offsetHeight - (this.options.contentHeight + this.options.zoomMargin) * this.scale;

    if (this.preserveArea) {
      const yPx = this.options.contentHeight * this.preserveArea.y * this.scale;
      maxY += Math.max(0, this.preserveArea.spaceAbove - yPx);
      minY -= Math.max(0, this.preserveArea.spaceBelow + yPx - this.options.contentHeight * this.scale);
    }

    if (minY > maxY) return (minY + maxY) / 2;
    return Math.max(minY, Math.min(maxY, y));
  }

  private clampScale(scale: number): number {
    return Math.min(Math.max(scale, this.fitScale), this.options.maxScale);
  }

  /**
   * Calculate the scale for a given zoom step.
   *
   * Scale is exponentially interpolated between fitScale (step 0)
   * and maxScale (step zoomSteps):
   *   scale = fitScale * (maxScale / fitScale) ^ (step / totalSteps)
   * This gives perceptually uniform zoom increments.
   */
  private calcScaleForZoomStep(zoomStep: ZoomStep | null): { zoomStep: number; scale: number } {
    const step = Math.max(0, Math.min(zoomStep ?? this.zoomStep, this.options.zoomSteps));
    const { fitScale } = this;
    const { maxScale, zoomSteps } = this.options;

    const scale = fitScale * Math.pow(maxScale / fitScale, step / zoomSteps);

    return { zoomStep: step, scale: this.clampScale(scale) };
  }

  /**
   * Inverse of calcScaleForZoomStep: find the nearest zoom step
   * for a given continuous scale value and sync internal state.
   */
  private syncZoomStep(): void {
    const { fitScale } = this;
    const { maxScale, zoomSteps } = this.options;

    // Invert: scale = fitScale * (maxScale/fitScale)^(step/steps)
    //       → step  = steps * log(scale/fitScale) / log(maxScale/fitScale)
    const ratio = Math.log(this.scale / fitScale) / Math.log(maxScale / fitScale);
    this.zoomStep = Math.round(ratio * zoomSteps);
  }

  /**
   * Unified drag handler for mouse, touch and pen via Pointer Events.
   * Uses setPointerCapture so move/up events are delivered to the
   * capturing element — no need for document-level listeners.
   * Aborts when a second pointer arrives (pinch-zoom takes over).
   */
  private setupDrag(): void {
    const activePointers = new Set<number>();
    let dragPointerId: number | null = null;
    let startX = 0, startY = 0, origX = 0, origY = 0;

    this.mapElem.addEventListener('pointerdown', (e: PointerEvent) => {
      activePointers.add(e.pointerId);

      // Only drag with a single pointer
      if (activePointers.size !== 1) {
        dragPointerId = null;
        return;
      }

      dragPointerId = e.pointerId;
      this.dragging = false;

      startX = e.pageX;
      startY = e.pageY;
      origX = this.x;
      origY = this.y;
    });

    this.mapElem.addEventListener('pointermove', (e: PointerEvent) => {
      if (e.pointerId !== dragPointerId) return;

      const dx = e.pageX - startX;
      const dy = e.pageY - startY;

      // Ignore micro-movements (< 2px) so taps aren't mistaken for drags
      if (!this.dragging && dx * dx + dy * dy < 4) {
        return;
      }

      // Capture pointer only once drag threshold is passed,
      // so taps still produce click events on SVG child elements
      if (!this.dragging) {
        this.mapElem.setPointerCapture(e.pointerId);
        this.mapElem.setAttribute('data-dragging', '');
      }

      this.dragging = true;

      const rawX = origX + dx;
      const rawY = origY + dy;
      this.x = this.clampX(rawX);
      this.y = this.clampY(rawY);

      // Re-anchor: if clamping ate part of the movement,
      // shift the origin so there is no dead zone when reversing direction.
      if (this.x !== rawX) {
        origX = this.x - dx;
      }
      if (this.y !== rawY) {
        origY = this.y - dy;
      }

      this.applyTransform(this.x, this.y);
    });

    const endDrag = (e: PointerEvent) => {
      activePointers.delete(e.pointerId);
      if (e.pointerId === dragPointerId) {
        dragPointerId = null;
      }
      if (activePointers.size === 0) {
        this.mapElem.removeAttribute('data-ddragging');
      }
    };

    this.mapElem.addEventListener('pointerup', endDrag);
    this.mapElem.addEventListener('pointercancel', endDrag);
  }

  /**
   * Wheel zoom: Ctrl+wheel (mouse) or trackpad pinch (fires wheel with ctrlKey).
   * Zooms toward the cursor position. Without Ctrl the event is ignored
   * so normal page scrolling works.
   */
  private setupWheelZoom(): void {
    this.rootElem.addEventListener('wheel', (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();

      const prevScale = this.scale;
      // deltaY is positive when zooming out (fingers apart → together)
      const factor = 1 - e.deltaY * 0.01;
      this.scale = this.clampScale(this.scale * factor);

      // Zoom toward cursor position
      const rect = this.rootElem.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;

      this.x = this.clampX(cursorX - (cursorX - this.x) * (this.scale / prevScale));
      this.y = this.clampY(cursorY - (cursorY - this.y) * (this.scale / prevScale));

      this.applyTransform(this.x, this.y, this.scale, 0);
      this.syncZoomStep();
    }, { passive: false });
  }

  /**
   * Setup two-finger pinch zoom
   */
  private setupPinchZoom(): void {

    let init1: Position | null = null;
    let init2: Position | null = null;
    let initD = 0;
    let initScale: number | null = null;

    this.mapElem.addEventListener('touchstart', (e: TouchEvent) => {
      const touches = e.touches.length;

      if (touches === 2) {
        this.dragging = false;
        this.mapElem.setAttribute('data-ddragging', '');

        init1 = { x: e.touches[0].pageX - this.x, y: e.touches[0].pageY - this.y };
        init2 = { x: e.touches[1].pageX - this.x, y: e.touches[1].pageY - this.y };
        initD = Math.sqrt(Math.pow(init1.x - init2.x, 2) + Math.pow(init1.y - init2.y, 2));
        initScale = this.scale;

        // Remove existing touchmove handler if any
        if (this.touchMoveHandler) {
          this.mapElem.removeEventListener('touchmove', this.touchMoveHandler);
        }

        this.touchMoveHandler = (e: TouchEvent) => {
          e.preventDefault();
          this.dragging = true;

          const pos = {
            x: (e.touches[0].pageX + e.touches[1].pageX) / 2,
            y: (e.touches[0].pageY + e.touches[1].pageY) / 2
          };

          const dist = Math.sqrt(
            Math.pow(e.touches[0].pageX - e.touches[1].pageX, 2) +
            Math.pow(e.touches[0].pageY - e.touches[1].pageY, 2)
          ) / initD;

          const scale = this.scale;
          this.scale = this.clampScale(initScale! * dist);

          const offset = this.getOffset();
          this.x = this.clampX(this.x - (pos.x - offset.left - this.x) * (this.scale / scale - 1));
          this.y = this.clampY(this.y - (pos.y - offset.top - this.y) * (this.scale / scale - 1));

          this.applyTransform(this.x, this.y, this.scale, 0);
        };

        this.touchEndHandler = () => {
          if (this.touchEndHandler) {
            document.removeEventListener('touchend', this.touchEndHandler);
          }
          if (this.touchMoveHandler) {
            this.mapElem.removeEventListener('touchmove', this.touchMoveHandler);
          }
          this.mapElem.removeAttribute('data-ddragging');
          this.syncZoomStep();
        };

        this.mapElem.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
        document.addEventListener('touchend', this.touchEndHandler);
      }
    }, { passive: false });
  }

  /**
   * Setup keyboard navigation (arrow keys to pan, +/- to zoom)
   * Only active when .mapplic-container has focus.
   */
  private setupKeyboardNavigation(): void {
    const panStep = 80;

    this.rootElem.addEventListener('keydown', (e: KeyboardEvent) => {
      let handled = true;

      switch (e.key) {
        case 'ArrowLeft':
          this.x = this.clampX(this.x + panStep);
          this.applyTransform(this.x, this.y, undefined, 100);
          break;
        case 'ArrowRight':
          this.x = this.clampX(this.x - panStep);
          this.applyTransform(this.x, this.y, undefined, 100);
          break;
        case 'ArrowUp':
          this.y = this.clampY(this.y + panStep);
          this.applyTransform(this.x, this.y, undefined, 100);
          break;
        case 'ArrowDown':
          this.y = this.clampY(this.y - panStep);
          this.applyTransform(this.x, this.y, undefined, 100);
          break;
        case '+':  
        case '=': {
          this.callbacks.onManualZoom?.();
          this.zoomIn();
          break;
        }
        case '-': {
          this.callbacks.onManualZoom?.();
          this.zoomOut();
          break;
        }
        case 'Escape':
          this.rootElem.blur();
          break;
        default:
          handled = false;
      }

      if (handled) {
        e.preventDefault();
      }
    });
  }
}
